import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, Zap, QrCode, CreditCard, ArrowDown } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { IMAGES, WHATSAPP_URL, scrollToId } from "../lib/config";
import { WhatsAppIcon } from "./Icons";

const TRUST_ICONS = [ShieldCheck, Zap, QrCode, CreditCard];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.12em] -mb-[0.12em]">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { t, lang } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.2]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-[100svh] overflow-hidden bg-[var(--ink)] pt-[108px] lg:pt-16" data-testid="hero">
      <div className="absolute left-0 top-[108px] h-[50svh] w-full overflow-hidden lg:inset-y-0 lg:h-auto lg:w-[128%]">
        <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
          <motion.img
            src={IMAGES.hero}
            alt="Jet ski cutting through the Atlantic off Tenerife"
            className="h-full w-full object-cover object-[50%_50%] lg:object-[30%_45%]"
            initial={{ opacity: 0, scale: 1.12 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.8, ease: [0.16, 1, 0.3, 1] }}
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B1120] via-[#0B1120]/15 to-transparent lg:hidden" />
      </div>
      <div className="absolute inset-0 hidden bg-gradient-to-t from-[#0B1120] via-[#0B1120]/55 to-[#0B1120]/15 lg:block" />
      <div className="absolute inset-0 hidden bg-gradient-to-r from-[#0B1120]/70 via-transparent to-transparent lg:block" />
      <div className="grain absolute inset-0" />

      <motion.div style={{ opacity: fade }} className="relative mx-auto flex min-h-[calc(100svh-108px)] max-w-7xl flex-col justify-start px-4 pb-14 pt-[40svh] sm:px-6 lg:min-h-[calc(100svh-64px)] lg:justify-end lg:px-8 lg:pb-24 lg:pt-0">
        <motion.p
          className="eyebrow !text-[#FFB4A2]"
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.3 }}
          data-testid="hero-eyebrow"
        >
          {t.hero.eyebrow}
        </motion.p>
        <h1 key={lang} className="font-display mt-4 max-w-4xl text-[2.15rem] font-extrabold leading-[1.02] tracking-[-0.03em] text-white sm:text-6xl lg:mt-5 lg:text-7xl" data-testid="hero-title">
          {t.hero.lines.map((line, i) => (
            <MaskedLine key={i} delay={0.45 + i * 0.14}>{i === t.hero.lines.length - 1 ? <span className="text-[var(--coral)]">{line}</span> : line}</MaskedLine>
          ))}
        </h1>
        <motion.p
          className="mt-5 max-w-xl text-[15px] leading-relaxed text-white/80 sm:text-lg lg:mt-6"
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.9 }}
          data-testid="hero-sub"
        >
          {t.hero.sub}
        </motion.p>
        <motion.div
          className="mt-7 flex flex-col gap-3 sm:flex-row lg:mt-8"
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 1.05 }}
        >
          <button type="button" onClick={() => scrollToId("trips")} className="btn-coral h-12 px-8 text-base sm:h-14" data-testid="hero-book-btn">{t.hero.ctaBook}</button>
          <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="btn-ghost h-12 px-8 text-base sm:h-14" data-testid="hero-whatsapp-btn">
            <WhatsAppIcon className="h-5 w-5 text-[var(--wa)]" />{t.hero.ctaWa}
          </a>
        </motion.div>
        <motion.ul
          className="mt-10 grid grid-cols-2 gap-x-6 gap-y-4 border-t border-white/15 pt-6 sm:grid-cols-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1.3 }}
          data-testid="trust-row"
        >
          {t.hero.trust.map((label, i) => {
            const Icon = TRUST_ICONS[i];
            return (
              <li key={i} className="flex items-center gap-2.5 text-sm font-medium text-white/90">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/10 ring-1 ring-white/20"><Icon className="h-4 w-4 text-[#FFB4A2]" /></span>
                {label}
              </li>
            );
          })}
        </motion.ul>
      </motion.div>

      <motion.button
        type="button"
        onClick={() => scrollToId("trips")}
        className="absolute bottom-6 right-6 hidden h-12 w-12 items-center justify-center rounded-full border border-white/25 text-white/80 lg:flex"
        animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
        aria-label={t.hero.scroll}
        data-testid="hero-scroll-btn"
      >
        <ArrowDown className="h-5 w-5" />
      </motion.button>
    </section>
  );
};
